import { nanoid } from 'nanoid';
import type { AgentToolDefinition, AgentToolInvoke } from '../config/types';
import { AgentToolCallStatus, type AgentToolCall } from './types';

export interface ToolInvokeOptions {
  chatId?: string;
}

type ToolInvokeDelegate = (
  name: string,
  input: Record<string, unknown>,
  options?: ToolInvokeOptions
) => Promise<AgentToolCall>;

export class ToolRegistry {
  private readonly tools = new Map<string, AgentToolDefinition>();
  private invokeDelegate?: ToolInvokeDelegate;

  constructor(definitions?: AgentToolDefinition[]) {
    definitions?.forEach((tool) => this.register(tool));
  }

  bindInvoke(delegate: ToolInvokeDelegate) {
    this.invokeDelegate = delegate;
  }

  invoke(name: string, input: Record<string, unknown> = {}, options?: ToolInvokeOptions) {
    if (!this.invokeDelegate) {
      throw new Error('Tool invocation is unavailable until AgentRuntime is mounted');
    }
    return this.invokeDelegate(name, input, options);
  }

  register(definition: AgentToolDefinition) {
    this.tools.set(definition.name, definition);
  }

  list() {
    return Array.from(this.tools.values());
  }

  get(name: string) {
    return this.tools.get(name);
  }

  async execute(
    name: string,
    input: Record<string, unknown>,
    ctx: Parameters<AgentToolInvoke>[1],
    existingCall?: AgentToolCall
  ): Promise<AgentToolCall> {
    const definition = this.tools.get(name);
    if (!definition || !definition.handler) {
      throw new Error(`Tool ${name} is not registered`);
    }

    const call: AgentToolCall = existingCall ?? {
      id: nanoid(),
      name,
      input,
      status: AgentToolCallStatus.Running
    };

    try {
      const output = await definition.handler(input, ctx);
      call.status = AgentToolCallStatus.Succeeded;
      call.output = output ?? {};
    } catch (error) {
      call.status = AgentToolCallStatus.Failed;
      call.error = (error as Error).message;
      throw error;
    }

    return call;
  }
}
